import { useEffect, useRef, useState, type ReactNode } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { joinCardioDuration, splitCardioDuration } from '@heraion/shared';

import type { useTheme } from './theme';
import { useFieldFocusReporter } from './useFieldFocus';

// Hours:minutes:seconds entry for cardio duration. Replaces typing raw seconds
// — impractical for a 45-minute run (Antenore, 2026-05-31) — with three small
// numeric fields. The joined total is stored back as a seconds STRING (the
// existing DraftSet.duration contract), empty when zero so buildPayload's
// empty-duration gate still drops an untouched cardio set. (The old note about
// sharing this with the card logger's CardioSetRow is stale — that component is
// gone; the timeline FocusPanel is the only surface left.)
export function CardioDurationField({
  seconds,
  onChange,
  editable = true,
  trailing,
  theme,
}: {
  /** Total duration as a raw-seconds string (DraftSet.duration). */
  seconds: string;
  onChange: (secondsStr: string) => void;
  editable?: boolean;
  /** Optional control at the end of the row, aligned to the input BOXES and
   *  not to the unit captions under them (MX-5 slice 2): the cardio details
   *  ☰ rides here instead of taking a row of its own. The three cells are
   *  flex:1, so they simply yield the width (88 → 73pt — still wide enough for
   *  three digits, and the boxes never touch). */
  trailing?: ReactNode;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  // These three cells hold the keyboard too (TM-18a): the resume must wait for
  // them exactly as it waits for reps and weight.
  const fieldFocus = useFieldFocusReporter();

  const total = parseInt(seconds, 10);
  const safe = Number.isFinite(total) && total > 0 ? total : 0;
  const parts = splitCardioDuration(safe);

  const fmt = (n: number) => (n > 0 ? String(n) : '');
  const [h, setH] = useState(fmt(parts.h));
  const [m, setM] = useState(fmt(parts.m));
  const [s, setS] = useState(fmt(parts.s));
  // Resync the local fields when `seconds` changes from OUTSIDE (the count-down
  // timer logging its held seconds, switching the bound set). lastEmitted guards
  // against our own echo clobbering a mid-edit field (e.g. clearing minutes to
  // retype it).
  const lastEmitted = useRef(safe);
  useEffect(() => {
    if (safe === lastEmitted.current) return;
    lastEmitted.current = safe;
    const next = splitCardioDuration(safe);
    setH(fmt(next.h));
    setM(fmt(next.m));
    setS(fmt(next.s));
  }, [safe]);

  const emit = (nh: string, nm: string, ns: string) => {
    const tot = joinCardioDuration(
      parseInt(nh, 10),
      parseInt(nm, 10),
      parseInt(ns, 10),
    );
    lastEmitted.current = tot;
    onChange(tot === 0 ? '' : String(tot));
  };

  const cell = (
    value: string,
    setter: (v: string) => void,
    unit: string,
    onAfter: (v: string) => void,
    maxLength: number,
    fieldId: string,
  ) => (
    <View style={styles.cell}>
      <TextInput
        style={[styles.input, !editable && styles.inputLocked]}
        keyboardType="number-pad"
        inputMode="numeric"
        placeholder="0"
        placeholderTextColor={theme.palette.ink3}
        value={value}
        onChangeText={(text) => {
          const clean = text.replace(/[^0-9]/g, '');
          setter(clean);
          onAfter(clean);
        }}
        onFocus={() => fieldFocus.onFocus(fieldId)}
        onBlur={() => fieldFocus.onBlur(fieldId)}
        maxLength={maxLength}
        editable={editable}
      />
      <Text style={styles.unit}>{unit}</Text>
    </View>
  );

  return (
    <View
      style={styles.row}
      accessibilityLabel={t('mobile.timeline.durationLabel', {
        defaultValue: 'Duration',
      })}
    >
      {cell(
        h,
        setH,
        t('mobile.timeline.hourShort', { defaultValue: 'h' }),
        (v) => emit(v, m, s),
        2,
        'cardio.dur.h',
      )}
      <Text style={styles.sep}>:</Text>
      {cell(
        m,
        setM,
        t('mobile.timeline.minShort', { defaultValue: 'min' }),
        (v) => emit(h, v, s),
        3,
        'cardio.dur.m',
      )}
      <Text style={styles.sep}>:</Text>
      {cell(
        s,
        setS,
        t('mobile.timeline.secShort', { defaultValue: 'sec' }),
        (v) => emit(h, m, v),
        2,
        'cardio.dur.s',
      )}
      {trailing ? <View style={styles.trailing}>{trailing}</View> : null}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: sp, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: sp.s2,
    },
    cell: { flex: 1, alignItems: 'center', gap: 2 },
    input: {
      alignSelf: 'stretch',
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: sp.s2,
      // Compacted to match the reps/weight value fields (Antenore 2026-07-02:
      // the cardio h:min:s cells looked oversized) — md font + s2 padding, was
      // lg + s3. The colon marginTop offsets the unit label below, so it stays.
      paddingVertical: sp.s2,
      color: p.ink,
      fontSize: ty.sizes.md,
      textAlign: 'center',
      backgroundColor: p.bg,
    },
    inputLocked: { color: p.ink3, backgroundColor: p.surface2 },
    unit: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
    },
    // Pulled up by the height of the unit caption below the boxes, so the
    // control lines up with the INPUTS rather than floating between the rows.
    trailing: { marginBottom: 18 },
    sep: {
      color: p.ink3,
      fontSize: ty.sizes.lg,
      fontWeight: '700',
      marginTop: -14, // visually centre the colon against the input boxes
    },
  });
}
